import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseOutlined, BellOutlined } from '@ant-design/icons';
import SbButton from 'uiCore/SbButton/SbButton';

import useNotificationStoreV2 from 'Store/useNotificationStoreV2';
import { clearAllPersistentNotifications } from 'Api/apiService';

import {
  PanelWrapperStyled,
  PanelHeaderStyled,
  PanelTitleStyled,
  PanelBodyStyled,
  GroupLabelStyled,
  EmptyStateStyled,
  NotifRowStyled,
  NotifTextBlockStyled,
  NotifTitleRowStyled,
  NotifTitleStyled,
  NotifTimeStyled,
  PanelFooterStyled,
} from './NotificationPanelV2.styles';
import { SbTooltip } from 'uiCore';
import {formatTime, getDayGroup, GROUP_ORDER } from 'Utils/timeFormat';
import useJobsStore from 'Store/useJobsStore';

// ─── Component ────────────────────────────────────────────────────────────────

const NotificationPanelV2 = ({ onClose }) => {
  const navigate = useNavigate();
  const setCompareViewSelection = useJobsStore((state) => state.setCompareViewSelection);
  const setSelectedJdTab = useJobsStore((state) => state.setSelectedJdTab);

  const { notifications, clearAll } = useNotificationStoreV2();


  const grouped = useMemo(() => {
    const map = {};
    notifications.forEach((n) => {
      const g = getDayGroup(n.created_at);
      if (!map[g]) map[g] = [];
      map[g].push(n);
    });
    return GROUP_ORDER.filter((g) => map[g]).map((g) => ({ label: g, items: map[g] }));
  }, [notifications]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleRowClick = (notif) => {
    const dest = notif.context_url || (notif.entity_id ? `/jobs-library/${notif.entity_id}` : null);
    const url = dest ? new URL(dest, window.location.origin) : null;
    const event = url?.searchParams.get('event');

    setCompareViewSelection('');
    setSelectedJdTab('job-description');

    onClose?.();

    if (event === 'APPROVAL_REQUESTED') {
      setCompareViewSelection('difference');
      if (dest) navigate(dest, { state: { viewMode: 'compare' } 
    });
    return;
    }

  if (dest) navigate(dest);
  };

  const handleClearAll = async () => {
    try { await clearAllPersistentNotifications(); } catch (_) {}
    clearAll();
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <PanelWrapperStyled>

      {/* Header */}
      <PanelHeaderStyled>
        <PanelTitleStyled>Notifications</PanelTitleStyled>
        <SbButton buttonType="action" size="small" icon={<CloseOutlined />} onClick={onClose} title="Close" />
      </PanelHeaderStyled>

      {/* Body */}
      <PanelBodyStyled>
        {grouped.length === 0 ? (
          <EmptyStateStyled>
            <BellOutlined />
            <p>No notifications yet</p>
          </EmptyStateStyled>
        ) : (
          grouped.map(({ label, items }) => (
            <div key={label}>
              <GroupLabelStyled>{label}</GroupLabelStyled>
              {items.map((notif) => (
                  <NotifRowStyled
                    key={notif.id}
                    onClick={() => handleRowClick(notif)}
                  >
                    <NotifTextBlockStyled>
                      <NotifTitleRowStyled>
                        <SbTooltip title={notif.message} placement='leftTop'>
                          <NotifTitleStyled>
                            {notif.message}
                          </NotifTitleStyled>
                        </SbTooltip>
                        <NotifTimeStyled>{formatTime(notif.created_at)}</NotifTimeStyled>
                      </NotifTitleRowStyled>
                    </NotifTextBlockStyled>

                  </NotifRowStyled>
              ))}
            </div>
          ))
        )}
      </PanelBodyStyled>

      {/* Footer */}
      {notifications.length > 0 && (
        <PanelFooterStyled>
          <SbButton buttonType="action" size="small" onClick={handleClearAll}>
            Clear all
          </SbButton>
        </PanelFooterStyled>
      )}

    </PanelWrapperStyled>
  );
};

export default NotificationPanelV2;
